import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Scale,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Target,
  PieChart,
  Sparkles,
  ShoppingCart,
  Loader2,
  ChevronRight,
  Brain,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Shield,
  Percent,
  DollarSign,
  BarChart3,
  LineChart
} from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Layout from '../components/Layout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTOR_COLORS = {
  "Banking": "#10b981",
  "Beverages": "#3b82f6",
  "Building & Construction": "#8b5cf6",
  "Energy": "#f59e0b",
  "Technology": "#06b6d4",
  "Transport": "#ef4444",
  "Investment": "#ec4899",
  "Agri-industrial": "#84cc16",
  "Agricultural": "#22c55e",
  "Other": "#6b7280"
};

export default function SmartRebalance() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [data, setData] = useState(null);
  const [selectedActions, setSelectedActions] = useState(new Set());

  const fetchRebalanceData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portfolio/rebalance`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch rebalance data:', error);
      toast.error(t('Failed to load rebalancing data', 'Imeshindwa kupakia data ya urekebishaji'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRebalanceData();
  }, [fetchRebalanceData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchRebalanceData();
  };

  const toggleAction = (index) => {
    const newSelected = new Set(selectedActions);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedActions(newSelected);
  };

  const selectAllActions = () => {
    if (selectedActions.size === data?.suggestions?.length) {
      setSelectedActions(new Set());
    } else {
      setSelectedActions(new Set(data?.suggestions?.map((_, i) => i)));
    }
  };

  const handleExecuteSelected = async () => {
    if (selectedActions.size === 0) {
      toast.error(t('Please select actions to execute', 'Tafadhali chagua vitendo vya kutekeleza'));
      return;
    }

    setExecuting(true);
    try {
      const actionsToExecute = data.suggestions.filter((_, i) => selectedActions.has(i));
      const response = await axios.post(`${API}/portfolio/rebalance/execute`, actionsToExecute);
      toast.success(t('Rebalancing simulation complete!', 'Uigaji wa urekebishaji umekamilika!'));
      setSelectedActions(new Set());
      fetchRebalanceData();
    } catch (error) {
      toast.error(t('Failed to execute rebalancing', 'Imeshindwa kutekeleza urekebishaji'));
    } finally {
      setExecuting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`;
    return amount?.toLocaleString() || '0';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-500 border-red-500/30 bg-red-500/10';
      case 'medium': return 'text-amber-500 border-amber-500/30 bg-amber-500/10';
      default: return 'text-muted-foreground border-border bg-muted/30';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 flex items-center justify-center">
            <Scale className="w-10 h-10 text-purple-500 animate-pulse" />
          </div>
          <p className="text-muted-foreground">{t('Analyzing your portfolio...', 'Inachambua portfolio yako...')}</p>
        </div>
      </Layout>
    );
  }

  const hasSuggestions = data?.suggestions?.length > 0;
  const currentAllocation = data?.current_allocation || [];
  const targetAllocation = data?.target_allocation || [];

  // Prepare chart data
  const comparisonData = currentAllocation.map(item => ({
    sector: item.sector,
    current: item.percentage,
    target: item.target,
    fill: SECTOR_COLORS[item.sector] || SECTOR_COLORS.Other
  }));

  return (
    <Layout>
      <div className="page-enter space-y-6 pb-8">
        {/* Hero Header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-900 via-purple-800 to-slate-900 border border-purple-700/50 p-6 md:p-8 text-white">
          <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-white/10 text-white border-white/20 backdrop-blur-sm">
                <Brain className="w-3 h-3 mr-1" />
                {t('AI-Powered Advisor', 'Mshauri wa AI')}
              </Badge>
              {data?.summary?.risk_profile && (
                <Badge variant="outline" className="border-white/20 text-white/70">
                  {t('Profile', 'Profaili')}: {data.summary.risk_profile}
                </Badge>
              )}
            </div>
            
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-bold mb-2">
                  {t('Smart Rebalancing', 'Urekebishaji wa Akili')}
                </h1>
                <p className="text-white/60 max-w-lg">
                  {t('AI-powered suggestions to optimize your portfolio allocation based on your risk profile and market conditions.',
                     'Mapendekezo ya AI kuboresha ugawaji wa portfolio yako kulingana na profaili yako ya hatari na hali ya soko.')}
                </p>
              </div>
              
              <Button 
                onClick={handleRefresh}
                variant="secondary"
                className="bg-white/10 text-white hover:bg-white/20 rounded-full"
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {t('Refresh Analysis', 'Sasisha Uchambuzi')}
              </Button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-white/10">
              <div className="text-center">
                <p className="text-3xl font-bold">{data?.summary?.total_trades || 0}</p>
                <p className="text-white/60 text-sm">{t('Suggested Trades', 'Biashara Zinazopendekezwa')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-400">+{data?.summary?.estimated_improvement || 0}</p>
                <p className="text-white/60 text-sm">{t('Health Score Boost', 'Ongezeko la Alama')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold">TZS {formatCurrency(data?.total_portfolio_value)}</p>
                <p className="text-white/60 text-sm">{t('Portfolio Value', 'Thamani ya Portfolio')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold">{currentAllocation.length}</p>
                <p className="text-white/60 text-sm">{t('Sectors Held', 'Sekta Zilizoshikiliwa')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Allocation Comparison */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Current vs Target Chart */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                {t('Current vs Target Allocation', 'Ugawaji wa Sasa dhidi ya Lengo')}
              </CardTitle>
              <CardDescription>
                {t('Compare your portfolio with the recommended allocation', 'Linganisha portfolio yako na ugawaji unaopendekezwa')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={comparisonData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" domain={[0, 50]} tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis dataKey="sector" type="category" tick={{ fill: 'hsl(var(--foreground))', fontSize: 12 }} width={100} />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [`${value}%`]}
                    />
                    <Bar dataKey="current" name={t('Current', 'Sasa')} fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="target" name={t('Target', 'Lengo')} fill="hsl(var(--muted-foreground))" radius={[0, 4, 4, 0]} opacity={0.5} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-primary" />
                  <span className="text-sm text-muted-foreground">{t('Current', 'Sasa')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-muted-foreground/50" />
                  <span className="text-sm text-muted-foreground">{t('Target', 'Lengo')}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Allocation Pie */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5 text-primary" />
                {t('Current Portfolio Mix', 'Mchanganyiko wa Portfolio ya Sasa')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={currentAllocation}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="percentage"
                      nameKey="sector"
                      label={({ sector, percentage }) => `${percentage}%`}
                      labelLine={false}
                    >
                      {currentAllocation.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={SECTOR_COLORS[entry.sector] || SECTOR_COLORS.Other} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        background: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [`${value}%`]}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              
              {/* Sector Legend */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                {currentAllocation.slice(0, 6).map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SECTOR_COLORS[item.sector] || SECTOR_COLORS.Other }} />
                    <span className="text-muted-foreground truncate">{item.sector}</span>
                    <span className="font-medium text-foreground ml-auto">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Rebalancing Suggestions */}
        <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  {t('Rebalancing Actions', 'Vitendo vya Urekebishaji')}
                </CardTitle>
                <CardDescription>
                  {hasSuggestions 
                    ? t('Select actions to optimize your portfolio', 'Chagua vitendo kuboresha portfolio yako')
                    : t('Your portfolio is well balanced!', 'Portfolio yako imesawazishwa vizuri!')
                  }
                </CardDescription>
              </div>
              
              {hasSuggestions && (
                <div className="flex items-center gap-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={selectAllActions}
                    className="rounded-full"
                  >
                    {selectedActions.size === data.suggestions.length 
                      ? t('Deselect All', 'Ondoa Chaguo Zote')
                      : t('Select All', 'Chagua Zote')
                    }
                  </Button>
                  <Button 
                    size="sm"
                    onClick={handleExecuteSelected}
                    disabled={selectedActions.size === 0 || executing}
                    className="rounded-full"
                    data-testid="execute-rebalance-btn"
                  >
                    {executing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('Simulating...', 'Inaiga...')}
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        {t('Execute Selected', 'Tekeleza Vilivyochaguliwa')} ({selectedActions.size})
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {hasSuggestions ? (
              data.suggestions.map((suggestion, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    selectedActions.has(index) 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border/50 hover:border-border'
                  }`}
                  onClick={() => toggleAction(index)}
                  data-testid={`suggestion-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <Checkbox 
                      checked={selectedActions.has(index)}
                      className="mt-1"
                    />
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {suggestion.action === 'sell' ? (
                          <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                            <ArrowDownRight className="w-5 h-5 text-red-500" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                            <ArrowUpRight className="w-5 h-5 text-emerald-500" />
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-lg text-foreground">{suggestion.symbol}</span>
                            <Badge className={`${getPriorityColor(suggestion.priority)} text-xs`}>
                              {suggestion.priority}
                            </Badge>
                            <Badge variant="outline" className={`text-xs ${
                              suggestion.action === 'sell' ? 'text-red-500 border-red-500/30' : 'text-emerald-500 border-emerald-500/30'
                            }`}>
                              {suggestion.action === 'sell' ? t('SELL', 'UZA') : t('BUY', 'NUNUA')}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{suggestion.reason}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 mt-3 p-3 rounded-lg bg-muted/30">
                        <div>
                          <p className="text-xs text-muted-foreground">{t('Shares', 'Hisa')}</p>
                          <p className="font-semibold text-foreground">
                            {suggestion.action === 'sell' ? '-' : '+'}{suggestion.shares_to_trade}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('Value', 'Thamani')}</p>
                          <p className="font-semibold text-foreground">
                            TZS {formatCurrency(suggestion.estimated_value)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('Sector', 'Sekta')}</p>
                          <p className="font-semibold text-foreground">{suggestion.sector}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {t('Portfolio is Optimized!', 'Portfolio Imeboreshwa!')}
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {t('Your current allocation aligns well with your risk profile. No major changes needed.',
                     'Ugawaji wako wa sasa unalingana vizuri na profaili yako ya hatari. Hakuna mabadiliko makubwa yanayohitajika.')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Important Notice */}
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-foreground mb-1">{t('Simulation Mode', 'Hali ya Uigaji')}</p>
              <p className="text-sm text-muted-foreground">
                {t('This is a simulation tool for educational purposes. To execute real trades, connect with a licensed DSE broker like Orbit Securities, Core Securities, or Vertex International.',
                   'Hii ni zana ya uigaji kwa madhumuni ya elimu. Kutekeleza biashara halisi, wasiliana na dalali aliyesajiliwa wa DSE kama Orbit Securities, Core Securities, au Vertex International.')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card 
            className="border-border/50 cursor-pointer hover:border-primary/30 transition-colors"
            onClick={() => navigate('/intelligence')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center">
                <Shield className="w-7 h-7 text-emerald-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">
                  {t('Portfolio Health Score', 'Alama ya Afya ya Portfolio')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('Check your full portfolio analysis', 'Angalia uchambuzi kamili wa portfolio yako')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
          
          <Card 
            className="border-border/50 cursor-pointer hover:border-primary/30 transition-colors"
            onClick={() => navigate('/goals')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 flex items-center justify-center">
                <Target className="w-7 h-7 text-cyan-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">
                  {t('Investment Goals', 'Malengo ya Uwekezaji')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('Track your financial goals', 'Fuatilia malengo yako ya kifedha')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
